// -------------------------------------
// Dependencies
// -------------------------------------
import iamForm from 'scripts/classes/iamForm';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { componentToName } from 'scripts/helpers/utilities';
import { Loadable, Disableable, Hidable } from 'scripts/classes/iamota-component-helpers';
import translate from 'scripts/helpers/iamota-translate';

// -------------------------------------
// Abstract Base Class: iamFormElement
// -------------------------------------
// Base class for form-related web components
// - Provides common functionality for form elements
// - Supports loading and disabled states
// - Abstract methods `getParent()` and `getParentName()` must be implemented in child classes
// -------------------------------------

class iamFormElement extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();

    // Variables
    this._form = null;
    this._iamForm = null;
    this.controls = [];
    this.control = null; // Populated if there is only one control (for convenience/clarity)
    this.loadable = new Loadable(this);
    this.disableable = new Disableable(this);
    this.hidable = new Hidable(this);
  }

  /**
   * Called when the element is added to the DOM.
   */
  connectedCallback() {
    // If this element is intentionally not associated with a form, it will not initialize at all (effectively a plain HTML element)
    if (!this.hasForm) return;
    if (this.hasForm && !this.form) {
      console.error("<".concat(this.tagName.toLowerCase(), "> is not associated with a <form> element"));
      this.disableable.setDisabled(true);
      return;
    }

    // Populate reference to this element's form controls
    // Note: if controls are dynamically added, this should be called again later
    this.refreshControls();
    iamotaEventHandler.subscribe("".concat(this.formName, ":init"), this.onParentFormInit.bind(this), {
      context: this.iamForm,
      source: "".concat("iamFormElement.js", "!").concat(this.namespace)
    });
    //iamotaEventHandler.trigger(`${this.namespace}:connected`, { iamFormElement: this }, { context: this, isAtomic: true });
  }

  /**
   * Initializes the element once the form is ready
   * Expected to be extended by child classes
   * @abstract
   */
  onParentFormInit() {
    this.onFormInit(...arguments);
    iamotaEventHandler.subscribe("".concat(this.formName, ":validation:init"), this.onParentFormValidationInit.bind(this), {
      context: this.iamForm,
      source: "".concat("iamFormElement.js", "!").concat(this.namespace)
    });
    iamotaEventHandler.trigger(["".concat(this.namespace, ":init"), "iamForm:".concat(this.elementName, ":init")], {
      iamFormElement: this
    }, {
      context: this,
      isAtomic: true
    });
  }

  /**
   * Initializes the element once the form is ready
   * Expected to be extended by child classes
   * @abstract
   */
  onFormInit() {
    // To be implemented by child class
  }

  /**
   * Initializes the element once the form is ready
   * Expected to be extended by child classes
   * @abstract
   */
  onParentFormValidationInit(_ref) {
    var {
      validator
    } = _ref;
    this.onFormValidationInit(...arguments);
    iamotaEventHandler.trigger(["".concat(this.namespace, ":validation:init"), "iamForm:".concat(this.elementName, ":validation:init")], {
      iamFormElement: this,
      validator
    }, {
      context: this,
      isAtomic: true
    });
  }

  /**
   * Initializes the element once the form is ready
   * Expected to be extended by child classes
   * @abstract
   */
  onFormValidationInit() {
    // To be implemented by child class
  }

  /**
   * Returns the element name (used in event handling)
   * @returns {string}
   */
  get elementName() {
    return componentToName(this.constructor.name);
  }

  /**
   * Returns true if the element has a form
   */
  get hasForm() {
    return this.dataset.formId !== 'n/a';
  }

  /**
   * Returns the associated <form> element
   * Assumes this form element is either nested within a form or has a `data-form-id` attribute pointing to it's associated form
   * @returns {Object}
   * @abstract
   */
  get form() {
    if (!this.hasForm) return null;

    // Cache it for future use (e.g. after detaching from the DOM during disconnectedCallback)
    if (!this._form) {
      this._form = document.querySelector("[data-form-id=\"".concat(this.dataset.formId, "\"] form")) || this.closest('form');
      if (!this._form) console.error("Unable to find a <form> associated with <".concat(this.tagName.toLowerCase(), ">"));
    }
    return this._form;
  }

  /**
   * Returns the associated iamForm Component
   */
  get iamForm() {
    if (!this.hasForm) return null;

    // Cache it for future use (e.g. after detaching from the DOM during disconnectedCallback)
    if (!this._iamForm) {
      var _this$form;
      var parentElement = (_this$form = this.form) === null || _this$form === void 0 ? void 0 : _this$form.parentElement;
      if (parentElement instanceof iamForm || /^iam-.*-form$/i.test(parentElement === null || parentElement === void 0 ? void 0 : parentElement.tagName)) {
        this._iamForm = parentElement;
      }
      if (!this._iamForm) console.error("Unable to find an iam-form instance associated with <".concat(this.tagName.toLowerCase(), ">"), this, parentElement);
    }
    return this._iamForm;
  }

  /**
   * Returns the name prefix for events (must be implemented by child class)
   * @returns {string}
   */
  get formName() {
    var _this$iamForm;
    return componentToName(((_this$iamForm = this.iamForm) === null || _this$iamForm === void 0 ? void 0 : _this$iamForm.tagName) || (this.hasForm ? 'unknownForm' : 'noForm'));
  }
  get namespace() {
    return "".concat(this.formName, ":").concat(this.elementName);
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnectedCallback() {
    //iamotaEventHandler.trigger(`${this.namespace}:disconnected`, { iamFormElement: this }, { context: this });
  }

  /**
   * Validate Required Attributes
   * @param {Array} attributeList - List of required attributes
   * @returns {boolean} - True if all required attributes are present
   */
  validateRequiredAttributes() {
    var attributeList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    if (!Array.isArray(attributeList)) {
      console.error('Unable to validate required attributes: Invalid attribute list');
      return false;
    }
    var isValid = true;
    attributeList.forEach(attribute => {
      if (!this[attribute]) {
        var _this$iamForm2;
        console.error("Unable to initialize the ".concat(this.namespace, ": Missing required \"").concat(attribute, "\" attribute"));
        (_this$iamForm2 = this.iamForm) === null || _this$iamForm2 === void 0 || _this$iamForm2.setErrorState(translate('An error occurred, please try refreshing the page or contact support', "".concat(this.namespace, ".error_missing_attribute")));
        isValid = false;
      }
    });
    return isValid;
  }
  getControls() {
    return this.querySelectorAll('input:not([type="hidden"]), select, textarea, button');
  }
  refreshControls() {
    this.controls = this.getControls();
    this.control = this.controls.length === 1 ? this.controls[0] : null;
  }

  /**
   * Expose Loading/Disabled/Hidden state methods
   */
  setLoading(isLoading) {
    this.loadable.setLoading(isLoading);
  }
  setDisabled(isDisabled) {
    this.disableable.setDisabled(isDisabled);
  }
  setHidden(isHidden) {
    this.hidable.setHidden(isHidden);
  }
  isLoading() {
    return this.loadable.isLoading();
  }
  isDisabled() {
    return this.disableable.isDisabled();
  }
  isHidden() {
    return this.hidable.isHidden();
  }
}
export default iamFormElement;